/**
 * Competition PDFs: ladder standings, a league cycle's table or grid, a
 * tournament and an americano cycle. They follow the meet printout's rules
 * (owner decision, 7 Oct 2026, see COMPLIANCE.html D144 to D146):
 *
 *   - club admins of the competition's club, and super admins, only;
 *   - one "Export PDF" dialog, with a line saying what the PDF holds, a
 *     preview of the real PDF, and "Before you print" with the permission
 *     tick (which gates Print and Download) and the under 18 tick;
 *   - under 18s, and anyone whose age the API does not know, are printed
 *     "First L." unless the under 18 tick asks for them in full.
 *
 * The preview is PAGE IMAGES of the same document (GET {id}/pdf/preview),
 * never the PDF: a PDF in the browser carries the browser's own Download and
 * Print, which would go round the tick. The PDF itself (GET {id}/pdf/file,
 * streamed, never stored) is fetched only when Print or Download is pressed
 * with the tick given.
 *
 * Pure. Imported explicitly (house style), never relied on as an auto import.
 */
import {endpoints} from '~/network/endpoints';
import {
  PRINT_JUNIOR_FULL_NAMES_LINE,
  PRINT_UNDER_18_LINE,
} from '~/composables/useMeetPrintout';

export const EXPORT_PDF_LABEL = 'Export PDF';
export const EXPORT_PDF_TITLE = 'Export PDF';
export const EXPORT_PDF_PRINT = 'Print';
export const EXPORT_PDF_DOWNLOAD = 'Download';
export const EXPORT_PDF_FAILED = "The PDF couldn't be made. Please try again.";

/** A league cycle prints as a table per division, or as a grid of every match. */
export const LEAGUE_PDF_TYPES = [
  {value: 'table', label: 'Table'},
  {value: 'matrix', label: 'Grid'},
];

const RESOURCES = {
  ladder: 'ladders',
  leagueCycle: 'leagueCycles',
  tournament: 'tournaments',
  americanoCycle: 'americanoCycles',
};

const endpointFor = (kind, format, id, {type = 'table', juniorFullNames = false} = {}) => {
  const resource = RESOURCES[format];
  if (!resource) throw new Error(`Unknown competition PDF format: ${format}`);
  return endpoints[resource][kind](id, {type: type === 'matrix' ? 'matrix' : 'table', juniorFullNames: juniorFullNames === true});
};

/**
 * The PDF itself, streamed. Fetch it ONLY from Print or Download, after the
 * permission tick. A league cycle defaults to its table.
 */
export const competitionPdfEndpoint = (format, id, opts) => endpointFor('pdfFile', format, id, opts);

/** The preview: page images of the same PDF, safe to show before the tick. */
export const competitionPdfPreviewEndpoint = (format, id, opts) => endpointFor('pdfPreview', format, id, opts);

/** One preview page, as an image source. Only a PNG is ever shown. */
export const previewPageSrc = (page) => `data:image/png;base64,${String(page ?? '')}`;

/** The preview's pages, or none for anything the API did not send as a list of strings. */
export const previewPages = (preview) =>
  Array.isArray(preview?.pages) ? preview.pages.filter((p) => typeof p === 'string' && p.length > 0) : [];

/**
 * Whether the API shortens anybody's name on this sheet (with juniors not in
 * full, whatever was asked). The under 18 tick is offered only then, as on the
 * meet printout.
 */
export const namesShortenedFromPreview = (preview) => preview?.namesShortened === true;

/** Said under a long preview, which shows only its first pages. */
export const previewMoreLine = (preview) => {
  const shown = previewPages(preview).length;
  const total = Number(preview?.pageCount ?? 0);
  return total > shown && shown > 0 ? `The preview shows the first ${shown} of ${total} pages. The PDF has them all.` : null;
};

/**
 * May this person export the competition's PDF? Super admins always; anyone
 * else only as an admin of the competition's club. Mirrors the API's
 * CompetitionPdfRules.MayExport, so a button never offers what the server
 * would refuse.
 */
export const canExportCompetitionPdf = (user, clubId) => {
  if (!user) return false;
  if (user.superAdmin === true) return true;
  return clubId != null && Array.isArray(user.adminFor) && user.adminFor.includes(clubId);
};


const WHAT_IT_SHOWS = {
  ladder: () => "the ladder standings: each place with the players' names, the latest move and how many challenges each has played",
  leagueCycle: (type) => type === 'matrix'
    ? "each division as a grid of every match, with the players' names and any scores already recorded"
    : "each division's table, with the players' names and their results so far",
  tournament: () => "each stage of the tournament, its group tables and matches with the players' names, and any scores already recorded",
  americanoCycle: () => "each division's standings and its matches by round, with the players' names and any scores already recorded",
};

/** One plain sentence saying what the PDF holds, then the under 18 rule (the meet printout's own words). */
export const competitionPdfDescription = (format, {type = 'table', juniorFullNames = false} = {}) => {
  const what = WHAT_IT_SHOWS[format]?.(type) ?? 'the competition';
  const names = juniorFullNames === true ? PRINT_JUNIOR_FULL_NAMES_LINE : PRINT_UNDER_18_LINE;
  return `This shows ${what}, with a QR code to the page on Tap Leagues. ${names}`;
};

/**
 * The file name, matching the API's own, for when the browser cannot read
 * the Content-Disposition header.
 */
export const competitionPdfFileName = (format, {name, type = 'table'} = {}) => {
  const clean = String(name ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Tap Leagues';
  if (format === 'ladder') return `${clean} standings.pdf`;
  if (format === 'leagueCycle') return `${clean} ${type === 'matrix' ? 'matrix' : 'table'}.pdf`;
  return `${clean}.pdf`;
};

/** The server's own reason (401 and 403 carry one in global), else a plain line. */
export const competitionPdfErrorMessage = (body) => body?.global?.[0] || EXPORT_PDF_FAILED;
